<template>
  <div class="w-[300px] flex-shrink-0">
    <div class="bg-white rounded-lg overflow-hidden shadow sticky top-4">
      <!-- Header Bar Placeholder -->
      <div class="h-7 bg-gray-700 rounded-t-lg flex items-center px-3 gap-2">
        <div class="w-2.5 h-2.5 rounded-full bg-red-500"></div>
        <div class="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
        <div class="w-2.5 h-2.5 rounded-full bg-green-600"></div>
      </div>

      <div class="flex items-center flex-col justify-center h-full py-6">
        <!-- Product Image Placeholder -->
        <div class="bg-gray-200 aspect-square rounded flex items-center justify-center w-32 h-24">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-10 h-8 text-gray-400" fill="currentColor" viewBox="0 0 16 16">
            <path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0"/>
            <path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1z"/>
          </svg>
        </div>

        <!-- Product title skeleton -->
        <div class="px-4 pt-3 w-full">
          <div class="h-2 bg-gray-200 rounded w-3/4 mx-auto"></div>
        </div>

        <!-- Price skeleton -->
        <div class="px-4 pt-2 w-full">
          <div class="h-2 bg-gray-300 rounded w-1/3 mx-auto"></div>
        </div>

        <div class="px-4 py-3 space-y-3">
          <!-- Buttons Container -->
          <div class="w-32 flex flex-col items-center justify-center gap-1">
            <!-- WhatsApp Button Above Add to Cart -->
            <button
              v-if="settings.enabled && isAbove"
              :style="buttonStyle"
              class="whatsapp-btn w-full h-6 text-sm font-medium flex items-center justify-center gap-2 transition-colors"
            >
              <svg class="w-2 h-2 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"></path>
              </svg>
              <span class="text-[8px]">{{ buttonText }}</span>
            </button>

            <!-- Add to Cart Skeleton Button -->
            <button
              v-if="!settings.hide_add_to_cart"
              class="w-full bg-gray-300 rounded h-6 flex items-center justify-center"
              disabled
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="w-2 h-2 text-gray-700" fill="currentColor" viewBox="0 0 16 16">
                <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .49.598l-1 5a.5.5 0 0 1-.465.401l-9.397.472L4.415 11H13a.5.5 0 0 1 0 1H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM3.102 4l.84 4.479 9.144-.459L13.89 4H3.102zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
              </svg>
            </button>

            <!-- WhatsApp Button Below Add to Cart -->
            <button
              v-if="settings.enabled && isBelow"
              :style="buttonStyle"
              class="whatsapp-btn w-full h-6 text-sm font-medium flex items-center justify-center gap-2 transition-colors"
            >
              <svg class="w-2 h-2 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"></path>
              </svg>
              <span class="text-[8px]">{{ buttonText }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  settings: {
    type: Object,
    required: true
  }
});

const buttonStyle = computed(() => ({
  backgroundColor: props.settings.bg_color,
  color: props.settings.text_color,
  borderRadius: props.settings.border_radius + 'px',
  '--hover-bg': props.settings.bg_hover_color,
  '--hover-text': props.settings.text_hover_color
}));

// Support both old and new position values
const isAbove = computed(() => {
  return props.settings.button_position === 'above' ||
         props.settings.button_position === 'above_add_to_cart';
});

const isBelow = computed(() => {
  return props.settings.button_position === 'below' ||
         props.settings.button_position === 'below_add_to_cart';
});

const buttonText = computed(() => {
  return (props.settings.button_text || 'Buy on WhatsApp').substring(0, 15);
});
</script>

<style scoped>
.whatsapp-btn:hover {
  background-color: var(--hover-bg) !important;
  color: var(--hover-text) !important;
}
</style>
